import { ChangeDetectorRef, Component, EventEmitter, inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription, interval, of } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { HeaderAction } from '../../../shared/components/page-header/page-header.component';
import { RolesService } from '../roles.service';
import { ModuleForm } from '../components/permission-selector/permission-selector.component';
import {
  AuditEntry,
  ImpactAnalysis,
  ModulePermission,
  PermissionDiff,
  RoleDetailResponse,
} from '../roles.model';
import { BannerVariant } from '../components/edit-mode-banner/edit-mode-banner.component';
import { FieldValidationState } from '../../users/users.model';
import { SidebarMode } from '../components/role-sidebar/role-sidebar.component';

@Component({
  selector: 'app-role-edit',
  standalone: false,
  templateUrl: './role-edit.component.html',
  styleUrls: ['./role-edit.component.scss'],
})
export class RoleEditComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private rolesService = inject(RolesService);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  roleId = '';
  role: RoleDetailResponse | null = null;
  isLoading = true;
  isSubmitting = false;
  concurrentEditDetected = false;
  concurrentEditBy = '';
  private loadedAt = '';

  form: FormGroup;
  nameValidationState: FieldValidationState = { status: 'idle' };

  diff: PermissionDiff = { added: [], removed: [] };
  impact: ImpactAnalysis = { affectedUsers: 0, gainAccess: 0, loseAccess: 0 };
  initialPermissionsMap: { [code: string]: boolean } = {};
  currentModules: ModuleForm[] = [];
  moduleCountsMap: { [name: string]: { granted: number; total: number } } = {};
  grantedCount = 0;
  auditEntries: AuditEntry[] = [];

  private nameSub?: Subscription;
  private pollSub?: Subscription;

  readonly cancelAction = new EventEmitter<void>();
  readonly saveAction = new EventEmitter<void>();

  get pageHeaderActions(): HeaderAction[] {
    return [
      { label: 'Cancel', variant: 'ghost', emitter: this.cancelAction },
      { label: this.isSubmitting ? 'Saving…' : 'Save changes', variant: 'primary', emitter: this.saveAction, disabled: !this.canSave() || this.isSubmitting },
    ];
  }

  get headerBreadcrumbSection(): string {
    return this.role
      ? `Administration / Roles & Permissions / ${this.role.name}`
      : 'Administration / Roles & Permissions';
  }

  constructor() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      active: [true],
    });
  }

  ngOnInit(): void {
    this.roleId = this.route.snapshot.paramMap.get('id') ?? '';
    this.cancelAction.subscribe(() => this.cancel());
    this.saveAction.subscribe(() => this.save());
    this.loadRole();
  }

  ngOnDestroy(): void {
    this.nameSub?.unsubscribe();
    this.pollSub?.unsubscribe();
  }

  // ── Data loading ──────────────────────────────────────────

  loadRole(): void {
    this.isLoading = true;
    this.rolesService.findOne(this.roleId).subscribe({
      next: (role) => {
        this.role = role;
        this.loadedAt = role.lastEditedAt;
        this.buildInitialPermissionsMap(role);
        this.form.patchValue({
          name: role.name,
          description: role.description,
          active: role.active,
        });
        this.isLoading = false;
        this.setupNameValidation();
        this.startPolling();
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private buildInitialPermissionsMap(role: RoleDetailResponse): void {
    const map: { [code: string]: boolean } = {};
    role.modules?.forEach(mod => {
      const perms = mod.permissions && Object.keys(mod.permissions).length > 0
        ? mod.permissions
        : this.legacyFlagsToPermissions(mod);
      Object.assign(map, perms);
    });
    this.initialPermissionsMap = map;
  }

  private legacyFlagsToPermissions(mod: ModulePermission): { [code: string]: boolean } {
    const r: { [code: string]: boolean } = {};
    switch (mod.name) {
      case 'User Management':
        r['users.view'] = mod.has_read; r['users.create'] = mod.has_create;
        r['users.update'] = mod.has_update; r['users.delete'] = mod.has_delete;
        r['users.suspend'] = mod.has_delete;
        break;
      case 'Roles & Permissions':
        r['roles.view'] = mod.has_read; r['roles.create'] = mod.has_create;
        r['roles.update'] = mod.has_update; r['roles.delete'] = mod.has_delete;
        break;
      case 'Geographic Nodes':
        r['nodes.view'] = mod.has_read; r['nodes.create'] = mod.has_create;
        r['nodes.update'] = mod.has_update; r['nodes.delete'] = mod.has_delete;
        break;
      case 'Channels':
        r['channels.view'] = mod.has_read;
        r['channels.create'] = mod.has_create;
        r['channels.update'] = mod.has_update;
        r['channels.keys'] = mod.has_update;
        break;
      case 'POS':
        r['pos.view'] = mod.has_read;
        r['pos.create'] = mod.has_create;
        r['pos.update'] = mod.has_update;
        r['pos.delete'] = mod.has_delete;
        break;
      case 'SKU Management':
        r['skus.view'] = mod.has_read;
        r['skus.create'] = mod.has_create;
        r['skus.update'] = mod.has_update;
        r['skus.delete'] = mod.has_delete;
        break;
      case 'Surveys':
        r['surveys.view'] = mod.has_read;
        r['surveys.create'] = mod.has_create;
        r['surveys.update'] = mod.has_update;
        r['surveys.delete'] = mod.has_delete;
        break;
      case 'Dispatch & Operations':
        r['dispatch.view'] = mod.has_read;
        r['dispatch.assign'] = mod.has_update;
        r['dispatch.close'] = mod.has_delete;
        break;
      case 'Reports & Analytics':
        r['reports.view'] = mod.has_read;
        r['reports.export'] = mod.has_update;
        break;
    }
    return r;
  }

  // ── Validation & polling ──────────────────────────────────

  private setupNameValidation(): void {
    this.nameSub?.unsubscribe();
    this.nameSub = this.form.get('name')!.valueChanges.pipe(
      tap((val: string) => {
        this.nameValidationState = val ? { status: 'checking' } : { status: 'idle' };
      }),
      debounceTime(500),
      switchMap((val: string) => {
        if (!val || val === this.role?.name) {
          this.nameValidationState = val ? { status: 'valid', message: '✓ Available' } : { status: 'idle' };
          return of(null);
        }
        return this.rolesService.checkNameUniqueness(val);
      }),
    ).subscribe({
      next: (res) => {
        if (!res) return;
        this.nameValidationState = res.available
          ? { status: 'valid', message: '✓ Available' }
          : {
            status: 'conflict',
            message: 'A role with this name already exists',
            conflictUser: res.role ? { full_name: res.role.name, id: res.role.id } : undefined,
          };
      },
      error: () => {
        this.nameValidationState = { status: 'error', message: 'Error checking name' };
      },
    });
  }

  private startPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(30_000).pipe(
      switchMap(() => this.rolesService.findOne(this.roleId)),
    ).subscribe({
      next: (fresh) => {
        if (fresh.lastEditedAt !== this.loadedAt) {
          this.concurrentEditDetected = true;
          this.concurrentEditBy = fresh.lastEditedBy || 'another user';
        }
      },
    });
  }

  // ── Permission selector callbacks ─────────────────────────

  onModulesChange(modules: ModuleForm[]): void {
    this.currentModules = modules;
  }

  onPermissionCountChange(count: number): void {
    this.grantedCount = count;
    this.cdr.detectChanges();
  }

  onModuleCountsChange(countsMap: { [name: string]: { granted: number; total: number } }): void {
    this.moduleCountsMap = countsMap;
    this.cdr.detectChanges();
  }

  get totalPermissionsCount(): number {
    return Object.values(this.moduleCountsMap).reduce((sum, m) => sum + m.total, 0);
  }

  onPermissionDiff(diff: { added: string[]; removed: string[] }): void {
    this.diff = diff;
    this.impact = {
      affectedUsers: this.role?.usersCount ?? 0,
      gainAccess: diff.added.length > 0 ? (this.role?.usersCount ?? 0) : 0,
      loseAccess: diff.removed.length > 0 ? (this.role?.usersCount ?? 0) : 0,
    };
  }

  // ── Derived UI state ──────────────────────────────────────

  get sidebarMode(): SidebarMode {
    if (this.diff.added.length > 0 || this.diff.removed.length > 0) return 'edit-changes';
    return 'edit-idle';
  }

  get bannerVariant(): BannerVariant {
    if (this.nameValidationState.status === 'conflict') return 'red';
    if (this.concurrentEditDetected) return 'amber';
    if (this.diff.added.length > 0 || this.diff.removed.length > 0) return 'amber';
    return 'teal';
  }

  get bannerMessage(): string {
    if (this.nameValidationState.status === 'conflict') return '1 issue blocking save';
    if (this.concurrentEditDetected) return 'Concurrent edit detected';
    const total = this.diff.added.length + this.diff.removed.length;
    if (total > 0) return `${total} unsaved change${total > 1 ? 's' : ''}`;
    return `Edit mode — changes affect ${this.role?.usersCount ?? 0} users`;
  }

  get bannerSubMessage(): string {
    if (this.nameValidationState.status === 'conflict') return this.nameValidationState.message ?? '';
    if (this.concurrentEditDetected) return `This role was modified by ${this.concurrentEditBy}`;
    return '';
  }

  get displayCode(): string {
    return this.role ? 'ROLE-' + this.role.id.substring(0, 5).toUpperCase() : '';
  }

  canSave(): boolean {
    return (
      !this.isSubmitting &&
      this.form.valid &&
      this.nameValidationState.status !== 'conflict' &&
      this.nameValidationState.status !== 'checking'
    );
  }

  // ── Actions ───────────────────────────────────────────────

  async save(): Promise<void> {
    if (!this.canSave()) return;

    this.isSubmitting = true;
    const payload = {
      name: this.form.value.name,
      description: this.form.value.description || '',
      active: this.form.value.active,
      modules: this.currentModules.map(m => ({
        name: m.name,
        has_read: m.has_read,
        has_create: m.has_create,
        has_update: m.has_update,
        has_delete: m.has_delete,
        permissions: m.permissions,
      })),
    };

    try {
      await firstValueFrom(this.rolesService.updateFull(this.roleId, payload));
      this.router.navigate(['/roles', this.roleId]);
    } catch (e: any) {
      if (e?.status === 409) {
        this.nameValidationState = { status: 'conflict', message: 'A role with this name already exists' };
      } else {
        console.error('Save failed', e);
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  cancel(): void {
    this.router.navigate(['/roles', this.roleId]);
  }
}
